/**
 * Hero Carousel Component - Vanilla JavaScript
 * Slider responsive et accessible sans dépendances externes
 */

// Polyfill simple pour l'attribut inert
if (!("inert" in HTMLElement.prototype)) {
    Object.defineProperty(HTMLElement.prototype, "inert", {
        enumerable: true,
        get: function () {
            return this.hasAttribute("inert");
        },
        set: function (value) {
            if (value) {
                this.setAttribute("inert", "");
                // Désactiver tous les éléments focusables
                const focusableElements = this.querySelectorAll(
                    'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])',
                );
                focusableElements.forEach((el) => {
                    el.setAttribute(
                        "data-was-tabindex",
                        el.getAttribute("tabindex") || "0",
                    );
                    el.setAttribute("tabindex", "-1");
                });
            } else {
                this.removeAttribute("inert");
                // Réactiver les éléments focusables
                const focusableElements = this.querySelectorAll(
                    "[data-was-tabindex]",
                );
                focusableElements.forEach((el) => {
                    const wasTabindex = el.getAttribute("data-was-tabindex");
                    el.removeAttribute("data-was-tabindex");
                    if (wasTabindex === "0") {
                        el.removeAttribute("tabindex");
                    } else {
                        el.setAttribute("tabindex", wasTabindex);
                    }
                });
            }
        },
    });
}

class HeroCarousel {
    constructor(element, options = {}) {
        this.carousel = element;
        this.slides = this.carousel.querySelectorAll(".hero-carousel__slide");
        this.currentSlide = 0;
        this.isPlaying = true;
        this.touchStartX = 0;
        this.touchEndX = 0;

        // Récupérer les données des pictos depuis l'attribut data
        this.slidesPictos = JSON.parse(
            this.carousel.getAttribute("data-slides-pictos") || "[]",
        );

        // Configuration par défaut
        this.config = {
            autoplay: this.isDesktop(), // Autoplay uniquement en desktop
            autoplaySpeed: 8000,
            speed: 1000,
            pauseOnHover: false,
            pauseOnFocus: false,
            showDots: true,
            showArrows: false, // Flèches désactivées
            fade: window.innerWidth < 768,
            swipe: true,
            desktopBreakpoint: 768, // Breakpoint pour considérer comme desktop
            ...options,
        };

        this.init();
    }

    // Méthode pour détecter si on est en desktop
    isDesktop() {
        return window.innerWidth >= 768;
    }

    init() {
        if (this.slides.length <= 1) return;

        this.createStructure();
        // this.createDots(); // Anciens dots désactivés
        this.bindInlineDots(); // Nouveaux dots inline
        // this.createArrows(); // Flèches désactivées
        this.bindEvents();
        this.handleAutoplayBasedOnScreenSize();
        this.updateAriaLabels();

        // Marquer comme initialisé
        this.carousel.classList.add("hero-carousel--initialized");

        // Afficher le premier slide
        this.goToSlide(0);
    }

    createStructure() {
        // Wrapper pour les slides
        const slidesWrapper = document.createElement("div");
        slidesWrapper.className = "hero-carousel__slides";

        // Déplacer tous les slides dans le wrapper
        this.slides.forEach((slide) => {
            slide.style.opacity = "0";
            slide.style.transition = `opacity ${this.config.speed}ms ease-in-out`;
            slidesWrapper.appendChild(slide);
        });

        this.carousel.appendChild(slidesWrapper);
        this.slidesWrapper = slidesWrapper;
    }

    createDots() {
        // Ne créer les dots que s'il y a plusieurs slides
        if (!this.config.showDots || this.slides.length <= 1) return;

        const dotsContainer = document.createElement("div");
        dotsContainer.className = "hero-carousel__dots";
        dotsContainer.setAttribute("role", "tablist");
        dotsContainer.setAttribute("aria-label", "Navigation du carrousel");

        this.slides.forEach((_, index) => {
            const dot = document.createElement("button");
            dot.className = "hero-carousel__dot";
            dot.setAttribute("type", "button");
            dot.setAttribute("role", "tab");
            dot.setAttribute("aria-selected", index === 0 ? "true" : "false");
            dot.setAttribute("aria-label", `Aller au slide ${index + 1}`);
            dot.setAttribute("tabindex", index === 0 ? "0" : "-1");

            // Ajouter le SVG du picto si disponible
            if (this.slidesPictos[index]) {
                dot.innerHTML = this.slidesPictos[index];
            }

            dot.addEventListener("click", () => this.goToSlide(index));

            dotsContainer.appendChild(dot);
        });

        this.carousel.appendChild(dotsContainer);
        this.dots = dotsContainer.querySelectorAll(".hero-carousel__dot");
    }

    bindInlineDots() {
        // Récupérer tous les dots inline de tous les slides
        this.inlineDots = this.carousel.querySelectorAll(
            ".hero-carousel__dot-inline",
        );

        // Ajouter les événements de clic
        this.inlineDots.forEach((dot) => {
            dot.addEventListener("click", (e) => {
                const slideIndex = parseInt(
                    e.currentTarget.getAttribute("data-slide-index"),
                );
                this.goToSlide(slideIndex);
                // Reset le timer d'autoplay quand on clique sur un dot
                this.resetAutoplay();
            });
        });
    }

    createArrows() {
        // Ne créer les flèches que s'il y a plusieurs slides ET qu'on est en desktop
        if (!this.config.showArrows || this.slides.length <= 1) return;

        // Bouton précédent
        const prevButton = document.createElement("button");
        prevButton.className =
            "hero-carousel__arrow hero-carousel__arrow--prev";
        prevButton.setAttribute("type", "button");
        prevButton.setAttribute("aria-label", "Slide précédent");
        prevButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;

        // Bouton suivant
        const nextButton = document.createElement("button");
        nextButton.className =
            "hero-carousel__arrow hero-carousel__arrow--next";
        nextButton.setAttribute("type", "button");
        nextButton.setAttribute("aria-label", "Slide suivant");
        nextButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;

        prevButton.addEventListener("click", () => this.prevSlide());
        nextButton.addEventListener("click", () => this.nextSlide());

        this.carousel.appendChild(prevButton);
        this.carousel.appendChild(nextButton);

        this.prevButton = prevButton;
        this.nextButton = nextButton;
    }

    bindEvents() {
        // Pause/reprise au survol (uniquement si autoplay activé)
        if (this.config.pauseOnHover) {
            this.carousel.addEventListener("mouseenter", () =>
                this.pauseAutoplay(),
            );
            this.carousel.addEventListener("mouseleave", () =>
                this.resumeAutoplay(),
            );
        }

        // Pause/reprise au focus (uniquement si autoplay activé)
        if (this.config.pauseOnFocus) {
            this.carousel.addEventListener("focusin", () =>
                this.pauseAutoplay(),
            );
            this.carousel.addEventListener("focusout", () =>
                this.resumeAutoplay(),
            );
        }

        // Navigation au clavier
        this.carousel.addEventListener("keydown", (e) => this.handleKeydown(e));

        // Gestion du swipe tactile
        if (this.config.swipe) {
            this.carousel.addEventListener(
                "touchstart",
                (e) => this.handleTouchStart(e),
                { passive: true },
            );
            this.carousel.addEventListener(
                "touchend",
                (e) => this.handleTouchEnd(e),
                { passive: true },
            );
        }

        // Gestion du bouton de scroll
        const scrollButton = this.carousel.querySelector(
            ".hero-carousel__scroll",
        );
        if (scrollButton) {
            scrollButton.addEventListener("click", () =>
                this.handleScrollClick(),
            );
        }

        // Pause quand la page n'est pas visible
        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.pauseAutoplay();
            } else {
                this.resumeAutoplay();
            }
        });

        // Responsive : mise à jour des flèches ET de l'autoplay
        window.addEventListener("resize", () => this.handleResize());
    }

    handleKeydown(e) {
        switch (e.key) {
            case "ArrowLeft":
                e.preventDefault();
                this.prevSlide();
                break;
            case "ArrowRight":
                e.preventDefault();
                this.nextSlide();
                break;
            case "Home":
                e.preventDefault();
                this.goToSlide(0);
                break;
            case "End":
                e.preventDefault();
                this.goToSlide(this.slides.length - 1);
                break;
        }
    }

    handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
    }

    handleSwipe() {
        const swipeThreshold = 50;
        const diff = this.touchStartX - this.touchEndX;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                this.nextSlide();
            } else {
                this.prevSlide();
            }
        }
    }

    handleScrollClick() {
        // Trouver la div suivante après le carousel
        const nextElement = this.carousel.nextElementSibling;

        if (nextElement) {
            // Scroll vers la div suivante avec un comportement fluide
            nextElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } else {
            // Si pas de div suivante, scroll vers le bas de la page
            window.scrollTo({
                top: this.carousel.offsetTop + this.carousel.offsetHeight,
                behavior: "smooth",
            });
        }
    }

    handleResize() {
        // Gérer l'autoplay selon la taille d'écran
        const shouldAutoplay = this.isDesktop();

        if (shouldAutoplay && !this.config.autoplay) {
            // Passer en desktop : activer l'autoplay
            this.config.autoplay = true;
            this.startAutoplay();
        } else if (!shouldAutoplay && this.config.autoplay) {
            // Passer en mobile : désactiver l'autoplay
            this.config.autoplay = false;
            this.stopAutoplay();
        }
    }

    // Nouvelle méthode pour gérer l'autoplay selon la taille d'écran
    handleAutoplayBasedOnScreenSize() {
        if (this.config.autoplay && this.slides.length > 1) {
            this.startAutoplay();
        } else {
            this.stopAutoplay();
        }
    }

    goToSlide(index) {
        // Masquer le slide actuel
        this.slides[this.currentSlide].style.opacity = "0";
        this.slides[this.currentSlide].setAttribute("inert", "");

        // Désactiver les dots du slide précédent
        const prevSlideDots = this.slides[this.currentSlide].querySelectorAll(
            ".hero-carousel__dot-inline",
        );
        prevSlideDots.forEach((dot) => {
            dot.setAttribute("tabindex", "-1");
        });

        // Mettre à jour l'index
        this.currentSlide = index;

        // Afficher le nouveau slide
        this.slides[this.currentSlide].style.opacity = "1";
        this.slides[this.currentSlide].removeAttribute("inert");

        // Activer les dots du slide actuel
        const currentSlideDots = this.slides[
            this.currentSlide
        ].querySelectorAll(".hero-carousel__dot-inline");
        currentSlideDots.forEach((dot) => {
            dot.removeAttribute("tabindex");
        });

        // Mettre à jour les dots inline
        if (this.inlineDots) {
            this.inlineDots.forEach((dot, i) => {
                const slideIndex = parseInt(
                    dot.getAttribute("data-slide-index"),
                );
                dot.classList.toggle(
                    "hero-carousel__dot-inline--active",
                    slideIndex === index,
                );
                dot.setAttribute(
                    "aria-selected",
                    slideIndex === index ? "true" : "false",
                );
            });
        }

        // Annoncer le changement pour les lecteurs d'écran
        this.announceSlideChange();

        // Déclencher un événement personnalisé
        this.carousel.dispatchEvent(
            new CustomEvent("slideChange", {
                detail: {
                    currentSlide: this.currentSlide,
                    totalSlides: this.slides.length,
                },
            }),
        );
    }

    nextSlide() {
        const nextIndex = (this.currentSlide + 1) % this.slides.length;
        this.goToSlide(nextIndex);
    }

    prevSlide() {
        const prevIndex =
            (this.currentSlide - 1 + this.slides.length) % this.slides.length;
        this.goToSlide(prevIndex);
    }

    startAutoplay() {
        // Ne démarrer l'autoplay que s'il y a plusieurs slides
        if (!this.config.autoplay || this.slides.length <= 1) return;

        // Arrêter l'autoplay existant s'il y en a un
        this.stopAutoplay();

        // Remettre isPlaying à true pour que l'autoplay fonctionne
        this.isPlaying = true;

        this.autoplayInterval = setInterval(() => {
            if (this.isPlaying) {
                this.nextSlide();
            }
        }, this.config.autoplaySpeed);
    }

    pauseAutoplay() {
        this.isPlaying = false;
    }

    resumeAutoplay() {
        // Ne reprendre l'autoplay que s'il y a plusieurs slides
        if (this.slides.length > 1) {
            this.isPlaying = true;
        }
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
        this.isPlaying = false;
    }

    resetAutoplay() {
        // Reset le timer d'autoplay en redémarrant complètement l'autoplay
        if (this.config.autoplay && this.slides.length > 1) {
            this.startAutoplay();
        }
    }

    updateAriaLabels() {
        this.carousel.setAttribute("aria-live", "polite");
        this.carousel.setAttribute("aria-atomic", "false");

        this.slides.forEach((slide, index) => {
            if (index !== this.currentSlide) {
                slide.setAttribute("inert", "");
                // Désactiver tous les éléments focusables dans les slides cachés
                const focusableElements = slide.querySelectorAll(
                    'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])',
                );
                focusableElements.forEach((el) => {
                    el.setAttribute("tabindex", "-1");
                });
            } else {
                slide.removeAttribute("inert");
                // Réactiver les éléments focusables dans le slide actuel
                const focusableElements =
                    slide.querySelectorAll('[tabindex="-1"]');
                focusableElements.forEach((el) => {
                    el.removeAttribute("tabindex");
                });
            }
        });
    }

    announceSlideChange() {
        // Créer une annonce pour les lecteurs d'écran
        const announcement = document.createElement("div");
        announcement.setAttribute("aria-live", "polite");
        announcement.setAttribute("aria-atomic", "true");
        announcement.className = "sr-only";
        announcement.textContent = `Slide ${this.currentSlide + 1} sur ${this.slides.length}`;

        document.body.appendChild(announcement);

        setTimeout(() => {
            if (document.body.contains(announcement)) {
                document.body.removeChild(announcement);
            }
        }, 1000);
    }

    destroy() {
        this.stopAutoplay();

        // Supprimer les événements
        this.carousel.removeEventListener("mouseenter", this.pauseAutoplay);
        this.carousel.removeEventListener("mouseleave", this.resumeAutoplay);
        this.carousel.removeEventListener("focusin", this.pauseAutoplay);
        this.carousel.removeEventListener("focusout", this.resumeAutoplay);
        this.carousel.removeEventListener("keydown", this.handleKeydown);
        this.carousel.removeEventListener("touchstart", this.handleTouchStart);
        this.carousel.removeEventListener("touchend", this.handleTouchEnd);

        // Nettoyer le DOM
        this.carousel.classList.remove("hero-carousel--initialized");
    }
}

// Initialisation automatique
document.addEventListener("DOMContentLoaded", function () {
    const heroCarousels = document.querySelectorAll(".hero-carousel");

    heroCarousels.forEach((carousel) => {
        // Vérifier qu'il n'est pas déjà initialisé
        if (!carousel.classList.contains("hero-carousel--initialized")) {
            new HeroCarousel(carousel);
        }
    });
});

// Export pour utilisation en module
if (typeof module !== "undefined" && module.exports) {
    module.exports = HeroCarousel;
}

// Export global pour utilisation directe
window.HeroCarousel = HeroCarousel;
